import {
  createAsyncThunk,
  createSlice,
  createSelector,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "@store/index";
import { trpc } from "@utils/api";
import type {
  ThreadDetail,
  ThreadBlock,
  AgentThought,
  AgentToolCall,
  DataBlock,
  ScheduledMessageSummary,
} from "skedyul-ui/ui";
import {
  SandboxConfigSchema,
  listSandboxScenariosWithContext,
  resolveDefaultSandboxScenarioId,
} from "skedyul-core/features/agents/sandbox";

// =============================================================================
// SANDBOX TRACE TYPES
// =============================================================================

/** Tool call status for sandbox */
export type SandboxToolCallStatus = "started" | "completed" | "failed";

/** Tool call entry from SSE stream */
export interface SandboxToolCall {
  toolCallId: string;
  toolName: string;
  /** AI SDK handle (e.g. update_prospect) — required for multi-turn tool replay */
  handle?: string;
  args: Record<string, unknown>;
  status: SandboxToolCallStatus;
  result?: string;
  durationMs?: number;
  dataBlocks?: DataBlock[];
  /** User-friendly display name (e.g., "Update Prospect Stage" instead of "system:crm:prospect:update:stage") */
  displayName?: string;
  /** Whether this is a skill:load call */
  isSkillLoad?: boolean;
  /** Skill handle for skill:load calls (e.g., "stage-management") */
  skillHandle?: string;
  /** Nested tool calls that belong to this skill */
  childToolCalls?: SandboxToolCall[];
  /** Tool names registered by this skill (from skill:load result) */
  skillToolNames?: string[];
  /** Thoughts that occurred while this skill was active */
  thoughts?: SandboxThought[];
  /** Provider-specific options (e.g., Gemini's thought_signature for tool call replay) */
  providerOptions?: Record<string, unknown>;
}

/** Thought entry from SSE stream */
export interface SandboxThought {
  content: string;
  durationMs?: number;
  isComplete: boolean;
}

/** Tool history entry for tracking tool calls across conversation turns */
export interface ToolHistoryEntry {
  toolCallId: string;
  toolName: string;
  /** AI SDK handle (e.g. load_skill) — required for Gemini tool replay */
  handle?: string;
  args: Record<string, unknown>;
  result: unknown;
  timestamp: number;
  providerOptions?: Record<string, unknown>;
}

const SKILL_LOAD_TOOL_NAMES = new Set(["system:skill:load", "load_skill"]);

function isSkillLoadToolName(toolName: string): boolean {
  return SKILL_LOAD_TOOL_NAMES.has(toolName);
}

function parseDisplayNameFromResult(result: unknown): string | undefined {
  if (!result) return undefined;
  if (typeof result === "object" && result !== null && "name" in result) {
    const name = (result as { name?: unknown }).name;
    if (typeof name === "string" && name.length > 0) return name;
  }
  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result) as { name?: string };
      if (typeof parsed.name === "string" && parsed.name.length > 0) {
        return parsed.name;
      }
    } catch {
      // ignore invalid JSON
    }
  }
  return undefined;
}

function extractSkillToolNames(result: unknown): string[] {
  if (!result || typeof result !== "object") return [];
  const tools = (result as { tools?: Array<{ tool?: string }> }).tools;
  if (!Array.isArray(tools)) return [];
  return tools
    .map((t) => t.tool)
    .filter(
      (name): name is string => typeof name === "string" && name.length > 0,
    );
}

/**
 * Recursively compares two objects and returns an array of JSON paths
 * where values differ. Used for highlighting changed mock context fields.
 */
function getChangedPaths(
  oldObj: unknown,
  newObj: unknown,
  prefix = "",
): string[] {
  const changes: string[] = [];

  // Handle nullish cases
  if (oldObj === newObj) return changes;
  if (oldObj === null || oldObj === undefined) {
    if (newObj !== null && newObj !== undefined) {
      changes.push(prefix || "root");
    }
    return changes;
  }
  if (newObj === null || newObj === undefined) {
    changes.push(prefix || "root");
    return changes;
  }

  // Handle primitives
  if (typeof oldObj !== "object" || typeof newObj !== "object") {
    if (oldObj !== newObj) {
      changes.push(prefix || "root");
    }
    return changes;
  }

  // Handle arrays
  if (Array.isArray(oldObj) && Array.isArray(newObj)) {
    const maxLen = Math.max(oldObj.length, newObj.length);
    for (let i = 0; i < maxLen; i++) {
      const path = prefix ? `${prefix}.${i}` : String(i);
      changes.push(...getChangedPaths(oldObj[i], newObj[i], path));
    }
    return changes;
  }

  // Handle objects
  if (!Array.isArray(oldObj) && !Array.isArray(newObj)) {
    const oldRecord = oldObj as Record<string, unknown>;
    const newRecord = newObj as Record<string, unknown>;
    const allKeys = new Set([
      ...Object.keys(oldRecord),
      ...Object.keys(newRecord),
    ]);
    for (const key of allKeys) {
      const path = prefix ? `${prefix}.${key}` : key;
      changes.push(...getChangedPaths(oldRecord[key], newRecord[key], path));
    }
    return changes;
  }

  // Type mismatch (one is array, other is object)
  changes.push(prefix || "root");
  return changes;
}

/** Group tool calls under skill:load parents using skill tool registries when possible */
export function groupSandboxToolCallsBySkill(
  toolCalls: SandboxToolCall[],
  thoughtsBySkill?: Map<string | null, SandboxThought[]>,
): SandboxToolCall[] {
  const skillLoads: SandboxToolCall[] = [];

  for (const tc of toolCalls) {
    const isSkillLoad = tc.isSkillLoad ?? isSkillLoadToolName(tc.toolName);
    if (isSkillLoad) {
      // Get thoughts associated with this skill
      const skillThoughts = thoughtsBySkill?.get(tc.skillHandle ?? null) || [];
      skillLoads.push({
        ...tc,
        isSkillLoad: true,
        childToolCalls: [],
        skillToolNames: tc.skillToolNames ?? extractSkillToolNames(tc.result),
        thoughts: skillThoughts.length > 0 ? skillThoughts : undefined,
      });
    }
  }

  if (skillLoads.length === 0) return toolCalls;

  const toolNameToSkill = new Map<string, SandboxToolCall>();
  for (const skill of skillLoads) {
    for (const toolName of skill.skillToolNames ?? []) {
      toolNameToSkill.set(toolName, skill);
    }
  }

  // Helper to find owner by exact match or suffix match
  // e.g., "update_prospect" matches "system:crm:prospect:update"
  const findOwner = (toolName: string): SandboxToolCall | undefined => {
    // Exact match first
    let owner = toolNameToSkill.get(toolName);
    if (owner) return owner;

    // Try suffix match: tool name with underscores replaced by colons
    const normalizedToolName = toolName.replace(/_/g, ":");
    for (const [registeredName, skill] of toolNameToSkill.entries()) {
      if (
        registeredName.endsWith(`:${normalizedToolName}`) ||
        registeredName.endsWith(`:${toolName}`)
      ) {
        return skill;
      }
      // Also check if registered name contains the tool handle pattern
      const toolParts = toolName.split("_");
      if (toolParts.length >= 2) {
        const pattern = toolParts.join(":");
        if (registeredName.includes(pattern)) {
          return skill;
        }
      }
    }
    return undefined;
  };

  const assignedIds = new Set<string>();

  // Assign by skill tool registry (handles parallel skill loads before tools run)
  for (const tc of toolCalls) {
    const isSkillLoad = tc.isSkillLoad ?? isSkillLoadToolName(tc.toolName);
    if (isSkillLoad) continue;
    const owner = findOwner(tc.toolName);
    if (owner) {
      owner.childToolCalls = owner.childToolCalls ?? [];
      owner.childToolCalls.push(tc);
      assignedIds.add(tc.toolCallId);
    }
  }

  // Fallback: sequential assignment for tools not in any registry
  let activeSkill: SandboxToolCall | null = null;
  for (const tc of toolCalls) {
    const isSkillLoad = tc.isSkillLoad ?? isSkillLoadToolName(tc.toolName);
    if (isSkillLoad) {
      activeSkill =
        skillLoads.find((s) => s.toolCallId === tc.toolCallId) ??
        skillLoads.find(
          (s) =>
            s.skillHandle === tc.skillHandle ||
            s.skillHandle === (tc.args.name as string | undefined),
        ) ??
        activeSkill;
      continue;
    }
    if (assignedIds.has(tc.toolCallId)) continue;
    if (activeSkill) {
      activeSkill.childToolCalls = activeSkill.childToolCalls ?? [];
      activeSkill.childToolCalls.push(tc);
      assignedIds.add(tc.toolCallId);
    }
  }

  const orphans = toolCalls.filter(
    (tc) =>
      !(tc.isSkillLoad ?? isSkillLoadToolName(tc.toolName)) &&
      !assignedIds.has(tc.toolCallId),
  );

  return [...skillLoads, ...orphans];
}

function mapApiToolCallToSandbox(entry: {
  toolCallId: string;
  toolName: string;
  handle?: string;
  displayName?: string;
  args: Record<string, unknown>;
  result: string | null;
  status: "completed" | "failed";
  durationMs?: number;
  dataBlocks?: DataBlock[];
  isSkillLoad?: boolean;
  skillHandle?: string;
  skillToolNames?: string[];
  providerOptions?: Record<string, unknown>;
}): SandboxToolCall {
  const isSkillLoad = entry.isSkillLoad ?? isSkillLoadToolName(entry.toolName);
  const skillHandle =
    entry.skillHandle ??
    (isSkillLoad ? (entry.args.name as string | undefined) : undefined);
  const displayName =
    entry.displayName ??
    (isSkillLoad ? parseDisplayNameFromResult(entry.result) : undefined);

  return {
    toolCallId: entry.toolCallId,
    toolName: entry.toolName,
    handle: entry.handle,
    args: entry.args,
    status: entry.status,
    durationMs: entry.durationMs,
    displayName,
    isSkillLoad,
    skillHandle,
    // Include result for skill loads - needed for restoreSkillToolsFromHistory
    result: entry.result ?? undefined,
    skillToolNames: isSkillLoad
      ? entry.skillToolNames ?? extractSkillToolNames(entry.result)
      : undefined,
    providerOptions: entry.providerOptions,
    dataBlocks: entry.dataBlocks,
  };
}

// =============================================================================
// V3 TYPES - Aligned with agent YAML schema
// =============================================================================

/** Persona voice format configuration */
export interface VoiceFormat {
  maxChars?: number;
  noEmojis?: boolean;
  noHyphens?: boolean;
  noBulletPoints?: boolean;
  maxQuestionsPerMessage?: number;
  noSignOffs?: boolean;
}

/** Persona configuration */
export interface PersonaConfig {
  name: string;
  voice: {
    style: string;
    format?: VoiceFormat;
  };
}

/** Skill binding in agent config */
export interface SkillBinding {
  skill: string;
  description: string;
  /** Auto-inject this skill every turn (instructions + tools in system prompt) */
  alwaysLoad?: boolean;
  version?: number;
  versions?: Array<{ version: number; weight: number }>;
  enabled?: boolean;
}

/** Agent tool reference (simplified - tools available before skills are loaded) */
export interface AgentToolRef {
  tool: string;
  description?: string;
}

/** @deprecated Use AgentToolRef instead */
export type BootstrapToolRef = AgentToolRef;

/** Events configuration */
export interface EventsConfig {
  subscribes: string[];
}

/** Working memory configuration */
export interface WorkingMemoryConfig {
  strategy: "rolling_summary" | "full" | "sliding_window";
  maxTokens: number;
  summarizeAt?: number;
}

/** External memory configuration */
export interface ExternalMemoryConfig {
  enabled: boolean;
  ttl: string;
}

/** Memory configuration */
export interface MemoryConfig {
  working: WorkingMemoryConfig;
  external?: ExternalMemoryConfig;
}

/** Message approval policy */
export interface MessageApprovalPolicyConfig {
  requiresApproval?: boolean;
}

/** Policies configuration */
export interface PoliciesConfig {
  messages?: {
    send?: MessageApprovalPolicyConfig;
    schedule?: MessageApprovalPolicyConfig;
  };
  tools?: {
    externalRequiresApproval?: boolean;
    systemRequiresApproval?: boolean;
  };
}

/** Retry configuration */
export interface RetryConfig {
  attempts: number;
  backoff: "exponential" | "linear";
}

/** Runtime configuration */
export interface RuntimeConfig {
  model: string;
  timeout: string;
  timezone?: string;
  retry: RetryConfig;
}

/** Agent context contact */
export interface AgentContextContact {
  id: string;
  name: string;
  subscription: {
    identifierValue: string;
    channelHandle: string;
  };
  associations?: Record<string, { id: string; data: Record<string, unknown> }>;
}

/** Agent context sender */
export interface AgentContextSender {
  kind: "contact" | "member";
  displayName: string;
  contact?: AgentContextContact;
}

/** Agent context - unified for sandbox and production */
export interface AgentContext {
  sender: AgentContextSender;
}

// Legacy type aliases for backwards compatibility
/** @deprecated Use AgentContextContact */
export type MockContextContact = AgentContextContact;
/** @deprecated Use AgentContextSender */
export type MockContextSender = AgentContextSender;
/** @deprecated Use AgentContext */
export type MockContext = AgentContext;

/** Sandbox scenario option (starting mock context) */
export interface SandboxScenarioOption {
  id: string;
  label: string;
  context: MockContext;
}

/** Sandbox configuration */
export interface SandboxConfig {
  enabled: boolean;
  mockContext?: AgentContext;
  context?: AgentContext;
  scenarios?: Record<
    string,
    { label?: string; context: AgentContext }
  >;
  defaultScenario?: string;
}

/** Prompt injections (system workflow rules, title enrichment, etc.) */
export interface PromptsConfig {
  system?: string;
  titleEnrichment?: string;
}

/** Response behavior (message limits, requireFinal, etc.) */
export interface ResponsesBehaviorConfig {
  maxImmediate?: number;
  maxScheduled?: number;
  /** @deprecated Use maxImmediate */
  maxIntermediate?: number;
  requireFinal?: boolean;
  allowSchedule?: boolean;
}

/** Scheduling behavior defaults */
export interface SchedulingBehaviorConfig {
  patterns?: Array<{
    trigger: string;
    description?: string;
    examples?: string[];
    defaultDelay?: { amount: number; unit: string; timeWindow?: string };
  }>;
  defaults?: {
    requiresApproval?: boolean;
    timeWindow?: string;
    cancelOnActivity?: boolean;
  };
}

/** Agent runtime behavior configuration */
export interface BehaviorConfig {
  responses?: ResponsesBehaviorConfig;
  scheduling?: SchedulingBehaviorConfig;
}

/** Time window behavior when a named window is active */
export interface TimeWindowBehaviorConfig {
  responseMode: "immediate" | "ack_and_schedule" | "schedule_only";
  prompt?: string;
  scheduleFor?: string;
}

/** Named time window policy */
export interface TimeWindowPolicyConfig {
  timezone: string;
  windows: Array<{
    startTime: number | { hour: number; minute?: number };
    endTime: number | { hour: number; minute?: number };
    days: string[];
  }>;
  behavior?: TimeWindowBehaviorConfig;
}

/** Fallback when no time window matches */
export interface TimeWindowDefaultConfig {
  responseMode: "immediate" | "ack_and_schedule" | "schedule_only";
  scheduleFor?: string;
  prompt?: string;
}

/** Full agent version config (v3 format) */
export interface AgentVersionConfig {
  $schema?: string;
  handle: string;
  name: string;
  version: string;
  description: string;
  persona?: PersonaConfig;
  skills: SkillBinding[];
  /** Tools available before any skill is loaded */
  tools?: AgentToolRef[];
  events?: EventsConfig;
  memory?: MemoryConfig;
  policies?: PoliciesConfig;
  runtime?: RuntimeConfig;
  sandbox?: SandboxConfig;
  prompts?: PromptsConfig;
  behavior?: BehaviorConfig;
  timeWindows?: Record<string, TimeWindowPolicyConfig>;
  timeWindowDefault?: TimeWindowDefaultConfig;
}

// =============================================================================
// LIST ITEM TYPES
// =============================================================================

/** Agent list item (from getAgents) */
export interface AgentListItem {
  id: string;
  handle: string | null;
  name: string;
  description: string;
  createdAt: Date;
  llmModel?: { modelId: string; name: string } | null;
}

/** Agent detail (from getAgent) */
export interface AgentDetail {
  id: string;
  handle: string | null;
  name: string;
  description: string;
  system: string;
  llmModelId: string;
  createdAt: Date;
  llmModel?: { modelId: string; name: string } | null;
}

/** Agent version list item */
export interface AgentVersionListItem {
  id: string;
  version: number | null;
  versionLabel: string | null;
  isPublished: boolean;
  trafficWeight: number;
  createdAt: string;
}

/** Agent version detail (from getAgentVersion) */
export interface AgentVersionDetail {
  id: string;
  agentId: string;
  version: number | null;
  versionLabel: string | null;
  isPublished: boolean;
  trafficWeight: number;
  config: AgentVersionConfig | null;
  createdAt: string;
}

/** Skill list item */
export interface SkillListItem {
  id: string;
  handle: string;
  name: string;
  description: string | null;
  createdAt: string;
}

/** Skill version detail */
export interface SkillVersionDetail {
  id: string;
  skillId: string;
  version: number;
  versionLabel: string | null;
  isPublished: boolean;
  config: SkillConfig | null;
}

/** Skill detail (flattened from API) */
export interface SkillDetail {
  id: string;
  handle: string;
  name: string;
  description: string | null;
  instructions: string;
  tools: SkillToolConfig[];
  examples: Array<{ input: string; reasoning?: string; output: string }>;
  tags: string[];
  createdAt: string;
}

/** Skill tool definition (v2 schema) */
export interface SkillToolConfig {
  tool: string;
  description?: string;
  overrides?: Record<string, unknown>;
  sandbox?: {
    mock?: unknown;
  };
  requiresApproval?: boolean;
}

/** Skill config (from YAML) */
export interface SkillConfig {
  handle: string;
  name: string;
  description?: string;
  instructions: string;
  tools?: SkillToolConfig[];
  crmContext?: Record<string, string[]>;
  examples?: Array<{
    input: string;
    reasoning?: string;
    output: string;
    tool_call?: string;
  }>;
  tags?: string[];
}

/** Tool option for dropdowns */
export interface ToolOption {
  id: string;
  name: string;
  displayName: string;
  description: string;
  kind: string;
  group?: string;
}

/** Tool binding configuration for agent tools */
export interface ToolBinding {
  tool: string;
  description: string;
  sandbox?: {
    mock?: unknown;
  };
}

/** Skill option for dropdowns */
export interface SkillOption {
  value: string;
  label: string;
  description?: string;
}

/** Skill version option for dropdowns */
export interface SkillVersionOption {
  value: string;
  label: string;
  version: number;
  isPublished: boolean;
}

/** LLM model option for dropdowns */
export interface ModelOption {
  value: string;
  label: string;
  description?: string;
}

/** Sandbox message with full trace support */
export interface SandboxMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  /** Completed thoughts for this message turn */
  thoughts?: SandboxThought[];
  /** Tool calls executed during this turn */
  toolCalls?: SandboxToolCall[];
  /** Data blocks returned by tools */
  dataBlocks?: DataBlock[];
  /** Whether this is a scheduled message (not sent immediately) */
  isScheduled?: boolean;
  /** ISO timestamp for when scheduled message will be sent */
  scheduledFor?: string;
}

/** Event subscription for an agent */
export interface EventSubscription {
  id: string;
  eventType: string;
  condition: string | null;
  description: string | null;
  isEnabled: boolean;
  agentId: string | null;
  triggerId: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// STATE INTERFACE
// =============================================================================

export interface AgentStateV3 {
  // Agent list
  agents: {
    isLoading: boolean;
    data: AgentListItem[];
  };

  // Current agent (basic info)
  currentAgent: {
    isLoading: boolean;
    data: AgentDetail | null;
    versions: AgentVersionListItem[];
    versionsLoading: boolean;
  };

  // Current version being edited
  currentVersion: {
    isLoading: boolean;
    isSaving: boolean;
    isPublishing: boolean;
    data: AgentVersionDetail | null;
    working: AgentVersionConfig | null;
  };

  // Skills list
  skills: {
    isLoading: boolean;
    data: SkillListItem[];
  };

  // Current skill being edited
  currentSkill: {
    isLoading: boolean;
    isSaving: boolean;
    data: SkillDetail | null;
    working: SkillConfig | null;
  };

  // Sandbox state (for version editor chat)
  sandbox: {
    isRunning: boolean;
    mockContext: MockContext | null;
    selectedScenarioId: string | null;
    scenarioOptions: SandboxScenarioOption[];
    messages: SandboxMessage[];
    // Live streaming state (reset after each turn)
    currentThought: string | null;
    currentToolCalls: SandboxToolCall[];
    pendingThoughts: SandboxThought[];
    pendingDataBlocks: DataBlock[];
    // Chat history for multi-turn context replay
    chatHistory: Array<{ role: "user" | "assistant"; content: string }>;
    // Tool history for multi-turn context replay (gives agent context of past tool calls)
    toolHistory: ToolHistoryEntry[];
    // Changed mock context paths for flash highlighting (cleared after animation)
    changedMockContextPaths: string[];
  };

  // Event subscriptions for current agent
  eventSubscriptions: {
    isLoading: boolean;
    data: EventSubscription[];
  };

  // Lookup data for dropdowns
  availableTools: ToolOption[];
  availableSkills: SkillOption[];
  skillVersionsBySkill: Record<string, SkillVersionOption[]>;
  llmModels: ModelOption[];
  toolsLoading: boolean;
  skillsLoading: boolean;
  skillVersionsLoading: boolean;
  modelsLoading: boolean;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: AgentStateV3 = {
  agents: {
    isLoading: false,
    data: [],
  },
  currentAgent: {
    isLoading: false,
    data: null,
    versions: [],
    versionsLoading: false,
  },
  currentVersion: {
    isLoading: false,
    isSaving: false,
    isPublishing: false,
    data: null,
    working: null,
  },
  skills: {
    isLoading: false,
    data: [],
  },
  currentSkill: {
    isLoading: false,
    isSaving: false,
    data: null,
    working: null,
  },
  sandbox: {
    isRunning: false,
    mockContext: null,
    selectedScenarioId: null,
    scenarioOptions: [],
    messages: [],
    currentThought: null,
    currentToolCalls: [],
    pendingThoughts: [],
    pendingDataBlocks: [],
    chatHistory: [],
    toolHistory: [],
    changedMockContextPaths: [],
  },
  eventSubscriptions: {
    isLoading: false,
    data: [],
  },
  availableTools: [],
  availableSkills: [],
  skillVersionsBySkill: {},
  llmModels: [],
  toolsLoading: false,
  skillsLoading: false,
  skillVersionsLoading: false,
  modelsLoading: false,
};

// =============================================================================
// ASYNC THUNKS - AGENTS
// =============================================================================

export const getAgents = createAsyncThunk("agentsV3/getAgents", async () => {
  const agents = await trpc.agents.getAgents.query();
  return agents.map((a) => ({
    id: a.id,
    handle: a.handle,
    name: a.name,
    description: a.description,
    createdAt: a.createdAt,
    llmModel: a.llmModel,
  }));
});

export const getAgent = createAsyncThunk(
  "agentsV3/getAgent",
  async (input: { id: string }) => {
    const agent = await trpc.agents.getAgent.query(input);
    return {
      id: agent.id,
      handle: agent.handle,
      name: agent.name,
      description: agent.description,
      system: agent.system,
      llmModelId: agent.llmModelId,
      createdAt: agent.createdAt,
      llmModel: agent.llmModel,
    };
  },
);

const DEFAULT_SYSTEM_PROMPT = `You are a helpful AI assistant. Your role is to assist users with their requests in a clear, accurate, and friendly manner.

Guidelines:
- Be concise and direct in your responses
- Ask clarifying questions when needed
- Provide accurate information based on available context`;

const DEFAULT_MODEL_ID = "openai/gpt-5-nano";

export const createAgent = createAsyncThunk(
  "agentsV3/createAgent",
  async (input: {
    name: string;
    handle?: string;
    description?: string;
    system?: string;
    llmModelId?: string;
  }) => {
    const result = await trpc.agents.createAgent.mutate({
      name: input.name,
      handle: input.handle,
      description: input.description ?? "",
      system: input.system ?? DEFAULT_SYSTEM_PROMPT,
      llmModelId: input.llmModelId ?? DEFAULT_MODEL_ID,
    });
    return result;
  },
);

export const updateAgent = createAsyncThunk(
  "agentsV3/updateAgent",
  async (input: {
    id: string;
    name: string;
    description: string;
    system: string;
    llmModelId: string;
  }) => {
    await trpc.agents.updateAgent.mutate(input);
    return input;
  },
);

export const deleteAgent = createAsyncThunk(
  "agentsV3/deleteAgent",
  async (input: { id: string }) => {
    await trpc.agents.deleteAgent.mutate(input);
    return input.id;
  },
);

// =============================================================================
// ASYNC THUNKS - EVENT SUBSCRIPTIONS
// =============================================================================

export const getAgentEventSubscriptions = createAsyncThunk(
  "agentsV3/getAgentEventSubscriptions",
  async (input: { agentId: string }) => {
    const subscriptions = await trpc.eventSubscriptions.list.query({
      agentId: input.agentId,
    });
    return subscriptions.map((s) => ({
      id: s.id,
      eventType: s.eventType,
      condition: s.condition,
      description: s.description,
      isEnabled: s.isEnabled,
      agentId: s.agentId,
      triggerId: s.triggerId,
      createdAt: String(s.createdAt),
      updatedAt: String(s.updatedAt),
    }));
  },
);

export const createAgentEventSubscription = createAsyncThunk(
  "agentsV3/createAgentEventSubscription",
  async (input: {
    agentId: string;
    eventType: string;
    condition?: string;
    description?: string;
    isEnabled?: boolean;
  }) => {
    const subscription = await trpc.eventSubscriptions.create.mutate(input);
    return {
      id: subscription.id,
      eventType: subscription.eventType,
      condition: subscription.condition,
      description: subscription.description,
      isEnabled: subscription.isEnabled,
      agentId: subscription.agentId,
      triggerId: subscription.triggerId,
      createdAt: String(subscription.createdAt),
      updatedAt: String(subscription.updatedAt),
    };
  },
);

export const updateAgentEventSubscription = createAsyncThunk(
  "agentsV3/updateAgentEventSubscription",
  async (input: {
    id: string;
    eventType?: string;
    condition?: string | null;
    description?: string | null;
    isEnabled?: boolean;
  }) => {
    const subscription = await trpc.eventSubscriptions.update.mutate(input);
    return {
      id: subscription.id,
      eventType: subscription.eventType,
      condition: subscription.condition,
      description: subscription.description,
      isEnabled: subscription.isEnabled,
      agentId: subscription.agentId,
      triggerId: subscription.triggerId,
      createdAt: String(subscription.createdAt),
      updatedAt: String(subscription.updatedAt),
    };
  },
);

export const deleteAgentEventSubscription = createAsyncThunk(
  "agentsV3/deleteAgentEventSubscription",
  async (input: { id: string }) => {
    await trpc.eventSubscriptions.delete.mutate(input);
    return input.id;
  },
);

export const toggleAgentEventSubscription = createAsyncThunk(
  "agentsV3/toggleAgentEventSubscription",
  async (input: { id: string }) => {
    const subscription =
      await trpc.eventSubscriptions.toggleEnabled.mutate(input);
    return {
      id: subscription.id,
      eventType: subscription.eventType,
      condition: subscription.condition,
      description: subscription.description,
      isEnabled: subscription.isEnabled,
      agentId: subscription.agentId,
      triggerId: subscription.triggerId,
      createdAt: String(subscription.createdAt),
      updatedAt: String(subscription.updatedAt),
    };
  },
);

// =============================================================================
// ASYNC THUNKS - VERSIONS
// =============================================================================

export const getAgentVersions = createAsyncThunk(
  "agentsV3/getAgentVersions",
  async (input: { agentId: string }) => {
    const result = await trpc.agents.listVersions.query(input);
    return result;
  },
);

export const getAgentVersion = createAsyncThunk(
  "agentsV3/getAgentVersion",
  async (input: { agentId: string; versionId: string }) => {
    const result = await trpc.agents.getAgentVersion.query(input);
    return {
      id: result.id,
      agentId: result.agentId,
      version: result.version,
      versionLabel: result.versionLabel,
      isPublished: result.isPublished,
      trafficWeight: result.trafficWeight,
      config: result.config as AgentVersionConfig | null,
      createdAt: result.createdAt,
    };
  },
);

export const uploadAgentVersion = createAsyncThunk(
  "agentsV3/uploadAgentVersion",
  async (input: { agentId: string; config: AgentVersionConfig }) => {
    const result = await trpc.agents.uploadVersion.mutate({
      agentId: input.agentId,
      config: input.config as unknown as Record<string, unknown>,
    });
    return result;
  },
);

export const createAgentVersion = createAsyncThunk(
  "agentsV3/createAgentVersion",
  async (input: { agentId: string; config: AgentVersionConfig }) => {
    const result = await trpc.agents.saveDraft.mutate({
      config: input.config as unknown as Record<string, unknown>,
    });
    return result;
  },
);

export const updateAgentVersion = createAsyncThunk(
  "agentsV3/updateAgentVersion",
  async (input: { versionId: string; config: AgentVersionConfig }) => {
    const result = await trpc.agents.updateDraft.mutate({
      versionId: input.versionId,
      config: input.config as unknown as Record<string, unknown>,
    });
    return result;
  },
);

export const publishAgentVersion = createAsyncThunk(
  "agentsV3/publishAgentVersion",
  async (input: { versionId: string }) => {
    const result = await trpc.agents.publishVersion.mutate(input);
    return result;
  },
);

export const deleteAgentVersion = createAsyncThunk(
  "agentsV3/deleteAgentVersion",
  async (input: { versionId: string }) => {
    await trpc.agents.deleteDraft.mutate(input);
    return input.versionId;
  },
);

// =============================================================================
// ASYNC THUNKS - LOOKUPS
// =============================================================================

export const getAvailableTools = createAsyncThunk(
  "agentsV3/getAvailableTools",
  async () => {
    const result = await trpc.agents.getToolMarketplace.query();
    const tools: ToolOption[] = [];

    for (const group of result.systemToolsByModel) {
      for (const tool of group.tools) {
        tools.push({
          id: tool.id,
          name: tool.name,
          displayName: tool.displayName,
          description: tool.description,
          kind: tool.kind,
          group: group.groupName,
        });
      }
    }

    for (const group of result.integrationsByApp) {
      for (const tool of group.tools) {
        tools.push({
          id: tool.id,
          name: tool.name,
          displayName: tool.displayName,
          description: tool.description,
          kind: tool.kind,
          group: group.groupName,
        });
      }
    }

    return tools;
  },
);

export const getLlmModels = createAsyncThunk(
  "agentsV3/getLlmModels",
  async () => {
    const models = await trpc.llm.listAll.query();
    return models.map((m) => ({
      value: m.modelId,
      label: m.name,
      description: m.description ?? undefined,
    }));
  },
);

export const getAvailableSkills = createAsyncThunk(
  "agentsV3/getAvailableSkills",
  async () => {
    const skills = await trpc.skills.list.query();
    return skills.map((s) => ({
      value: s.handle,
      label: s.name,
      description: s.description ?? undefined,
    }));
  },
);

export const getSkillVersions = createAsyncThunk(
  "agentsV3/getSkillVersions",
  async (input: { skillHandle: string }) => {
    const skill = await trpc.skills.getByHandle.query({
      handle: input.skillHandle,
    });
    const versions = await trpc.skills.listVersions.query({
      skillId: skill.id,
    });
    return {
      skillHandle: input.skillHandle,
      versions: versions.map((v) => ({
        value: String(v.version),
        label: v.versionLabel || `v${v.version}`,
        version: v.version,
        isPublished: v.isPublished,
      })),
    };
  },
);

export const getSkills = createAsyncThunk("agentsV3/getSkills", async () => {
  const skills = await trpc.skills.list.query();
  return skills.map((s) => ({
    id: s.id,
    handle: s.handle,
    name: s.name,
    description: s.description ?? null,
    createdAt: s.createdAt,
  }));
});

export const getSkill = createAsyncThunk(
  "agentsV3/getSkill",
  async (input: { id: string }) => {
    const skill = await trpc.skills.get.query(input);
    return {
      id: skill.id,
      handle: skill.handle,
      name: skill.name,
      description: skill.description ?? null,
      instructions: skill.instructions ?? "",
      tools: (skill.tools ?? []) as SkillToolConfig[],
      examples: skill.examples ?? [],
      tags: skill.tags ?? [],
      createdAt: skill.createdAt,
    };
  },
);

export const createSkill = createAsyncThunk(
  "agentsV3/createSkill",
  async (input: {
    handle: string;
    name: string;
    description?: string;
    instructions?: string;
    tools?: SkillToolConfig[];
    examples?: Array<{ input: string; reasoning?: string; output: string }>;
    tags?: string[];
  }) => {
    const result = await trpc.skills.create.mutate(input);
    return {
      id: result.id,
      handle: result.handle,
      name: result.name,
      description: result.description ?? null,
      instructions: result.instructions ?? "",
      tools: (result.tools ?? []) as SkillToolConfig[],
      examples: result.examples ?? [],
      tags: result.tags ?? [],
      createdAt: result.createdAt,
    };
  },
);

export const updateSkill = createAsyncThunk(
  "agentsV3/updateSkill",
  async (input: {
    id: string;
    handle?: string;
    name?: string;
    description?: string;
    instructions?: string;
    tools?: SkillToolConfig[];
    examples?: Array<{ input: string; reasoning?: string; output: string }>;
    tags?: string[];
  }) => {
    const result = await trpc.skills.update.mutate(input);
    return {
      id: result.id,
      handle: result.handle,
      name: result.name,
      description: result.description ?? null,
      instructions: result.instructions ?? "",
      tools: (result.tools ?? []) as SkillToolConfig[],
      examples: result.examples ?? [],
      tags: result.tags ?? [],
      createdAt: result.createdAt,
    };
  },
);

export const deleteSkill = createAsyncThunk(
  "agentsV3/deleteSkill",
  async (input: { id: string }) => {
    await trpc.skills.delete.mutate(input);
    return input.id;
  },
);

// Type for sent messages from SSE stream (API response)
interface SentMessageData {
  id?: string;
  rawContent: string;
  transformedContent?: string;
  type: "intermediate" | "final" | "scheduled";
  scheduledFor?: string;
  timestamp?: string;
  index?: number;
}

// Processed sent message for display
interface ProcessedSentMessage {
  id: string;
  content: string;
  type: "intermediate" | "final" | "scheduled";
  timestamp: string;
  index: number;
  /** ISO timestamp for when scheduled message will be sent */
  scheduledFor?: string;
}

/** SSE event data structure from the API */
interface SandboxSSEEvent {
  message?: string;
  thought?: string;
  thoughtComplete?: boolean;
  durationMs?: number;
  toolCall?: {
    name: string;
    /** User-friendly display name for the tool */
    displayName?: string;
    status: "started" | "completed" | "failed";
    args?: Record<string, unknown>;
    durationMs?: number;
    resultSummary?: string;
  };
  data?: DataBlock[];
  thoughts?: Array<{ content: string; durationMs?: number }>;
  toolCalls?: Array<{
    toolCallId: string;
    toolName: string;
    displayName?: string;
    args: Record<string, unknown>;
    result: string | null;
    status: "completed" | "failed";
    durationMs?: number;
    dataBlocks?: DataBlock[];
    isSkillLoad?: boolean;
    skillHandle?: string;
    skillToolNames?: string[];
  }>;
  done?: boolean;
  error?: string;
  /** Updated context - new field name */
  updatedContext?: AgentContext;
  /** @deprecated Use updatedContext */
  updatedMockContext?: AgentContext;
  /** Sent messages from explicit message mode */
  sentMessages?: SentMessageData[];
}

export const sendSandboxChatMessage = createAsyncThunk<
  {
    assistantContent: string;
    mockContext: MockContext | null;
    thoughts: SandboxThought[];
    toolCalls: SandboxToolCall[];
    dataBlocks: DataBlock[];
    newToolHistory: ToolHistoryEntry[];
    sentMessages?: ProcessedSentMessage[];
  },
  {
    agentId: string;
    agentVersionId: string;
    message: string;
    mockContext: MockContext | null;
    priorMessages: SandboxMessage[];
  },
  { state: RootState; dispatch: AppDispatch }
>(
  "agentsV3/sendSandboxChatMessage",
  async (
    { agentId, agentVersionId, message, mockContext, priorMessages },
    { dispatch, getState },
  ) => {
    const chatHistory = priorMessages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        role: m.role as "user" | "assistant",
        content: m.content,
      }));

    // Get existing tool history from state
    const existingToolHistory = (getState().agentsV3 as AgentStateV3).sandbox
      .toolHistory;

    const response = await fetch("/api/agents/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        prompt: message,
        agentId,
        agentVersionId,
        sandbox: true,
        context: mockContext ?? undefined,
        sandboxScenarioId:
          (getState().agentsV3 as AgentStateV3).sandbox.selectedScenarioId ??
          undefined,
        chatHistory,
        toolHistory:
          existingToolHistory.length > 0 ? existingToolHistory : undefined,
        tabId: `sandbox-${agentVersionId}`,
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error("Failed to start sandbox agent stream");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let assistantContent = "";
    let updatedMockContext: MockContext | null = mockContext;
    const collectedThoughts: SandboxThought[] = [];
    const collectedToolCalls: SandboxToolCall[] = [];
    const collectedDataBlocks: DataBlock[] = [];
    const collectedSentMessages: ProcessedSentMessage[] = [];

    // Track active skill for associating thoughts with skills
    let activeSkillHandle: string | null = null;
    const thoughtsBySkill = new Map<string | null, SandboxThought[]>();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const jsonPart = line.slice(6);

        try {
          const data = JSON.parse(jsonPart) as SandboxSSEEvent;

          if (data.error) {
            throw new Error(data.error);
          }

          // Handle incremental thought updates
          if (data.thought !== undefined) {
            dispatch(
              agentSliceV3.actions.updateSandboxThought({
                thought: data.thought,
                complete: data.thoughtComplete ?? false,
                durationMs: data.durationMs,
              }),
            );
            if (data.thoughtComplete) {
              const thought: SandboxThought = {
                content: data.thought,
                durationMs: data.durationMs,
                isComplete: true,
              };
              collectedThoughts.push(thought);

              // Associate thought with active skill
              const skillKey = activeSkillHandle;
              const existing = thoughtsBySkill.get(skillKey) || [];
              thoughtsBySkill.set(skillKey, [...existing, thought]);
            }
          }

          // Handle tool call status updates
          if (data.toolCall !== undefined) {
            const isSkillLoad =
              data.toolCall.name === "system:skill:load" ||
              data.toolCall.name === "load_skill";
            // For skill:load calls, extract skill handle from args
            const skillHandle =
              isSkillLoad && data.toolCall.args
                ? (data.toolCall.args.name as string | undefined)
                : undefined;

            // Track active skill when a skill:load starts
            if (
              isSkillLoad &&
              data.toolCall.status === "started" &&
              skillHandle
            ) {
              activeSkillHandle = skillHandle;
            }

            dispatch(
              agentSliceV3.actions.updateSandboxToolCall({
                name: data.toolCall.name,
                displayName: data.toolCall.displayName,
                status: data.toolCall.status,
                args: data.toolCall.args,
                durationMs: data.toolCall.durationMs,
                resultSummary: data.toolCall.resultSummary,
                isSkillLoad,
                skillHandle,
              }),
            );
            if (
              data.toolCall.status === "completed" ||
              data.toolCall.status === "failed"
            ) {
              const displayName =
                data.toolCall.displayName ??
                (isSkillLoad
                  ? parseDisplayNameFromResult(data.toolCall.resultSummary)
                  : undefined);
              const skillToolNames = isSkillLoad
                ? extractSkillToolNames(data.toolCall.resultSummary)
                : undefined;

              collectedToolCalls.push({
                toolCallId: `tc-${Date.now()}-${collectedToolCalls.length}`,
                toolName: data.toolCall.name,
                displayName,
                args: data.toolCall.args ?? {},
                status: data.toolCall.status,
                durationMs: data.toolCall.durationMs,
                // Include result for skill loads - needed for restoreSkillToolsFromHistory
                result: data.toolCall.resultSummary,
                isSkillLoad,
                skillHandle,
                skillToolNames,
                // Attach data blocks from the same event to this tool call
                dataBlocks: data.data,
              });
            }
          }

          // Handle data blocks
          if (data.data !== undefined && data.data.length > 0) {
            dispatch(agentSliceV3.actions.addSandboxDataBlocks(data.data));
            collectedDataBlocks.push(...data.data);
          }

          // Handle message content
          if (typeof data.message === "string" && data.message) {
            assistantContent = data.message;
          }

          // Handle final event with authoritative data
          if (data.done) {
            // Handle explicit message mode: collect sentMessages for separate bubbles
            if (
              data.sentMessages &&
              Array.isArray(data.sentMessages) &&
              data.sentMessages.length > 0
            ) {
              // Store messages for separate display (including scheduled messages)
              for (const msg of data.sentMessages) {
                collectedSentMessages.push({
                  id: msg.id || crypto.randomUUID(),
                  content: msg.transformedContent || msg.rawContent,
                  type: msg.type,
                  timestamp: msg.timestamp || new Date().toISOString(),
                  index: msg.index ?? collectedSentMessages.length,
                  scheduledFor: msg.scheduledFor,
                });
              }
              // Also set assistantContent for backwards compatibility (last non-scheduled message)
              const lastNonScheduled = collectedSentMessages
                .filter((m) => m.type !== "scheduled")
                .pop();
              if (lastNonScheduled) {
                assistantContent = lastNonScheduled.content;
              }
            }

            // Check for updated context (new field name first, then deprecated name)
            if (data.updatedContext) {
              updatedMockContext = data.updatedContext;
            } else if (data.updatedMockContext) {
              updatedMockContext = data.updatedMockContext;
            }
            // Use authoritative thoughts/toolCalls from done event if present
            if (data.thoughts && data.thoughts.length > 0) {
              collectedThoughts.length = 0;
              // Clear skill-based thoughts since we're using authoritative data
              thoughtsBySkill.clear();
              for (const t of data.thoughts) {
                const thought: SandboxThought = {
                  content: t.content,
                  durationMs: t.durationMs,
                  isComplete: true,
                };
                collectedThoughts.push(thought);
                // For authoritative thoughts, put them at agent level (null skill)
                const existing = thoughtsBySkill.get(null) || [];
                thoughtsBySkill.set(null, [...existing, thought]);
              }
            }
            if (data.toolCalls && data.toolCalls.length > 0) {
              collectedToolCalls.length = 0;
              for (const entry of data.toolCalls) {
                collectedToolCalls.push(mapApiToolCallToSandbox(entry));
              }
            }
          }
        } catch (parseError) {
          if (
            parseError instanceof Error &&
            parseError.message !== "Unexpected end of JSON input"
          ) {
            throw parseError;
          }
        }
      }
    }

    // Get agent-level thoughts (not associated with any skill)
    const agentLevelThoughts = thoughtsBySkill.get(null) || [];

    // Convert collected tool calls to tool history entries for multi-turn context
    const newToolHistory: ToolHistoryEntry[] = collectedToolCalls.map((tc) => ({
      toolCallId: tc.toolCallId,
      toolName: tc.toolName,
      handle: tc.handle,
      args: tc.args,
      result: tc.result ?? null,
      timestamp: Date.now(),
      providerOptions: tc.providerOptions,
    }));

    const groupedToolCalls = groupSandboxToolCallsBySkill(
      collectedToolCalls,
      thoughtsBySkill,
    );
    return {
      assistantContent:
        assistantContent || "The agent did not return a response.",
      mockContext: updatedMockContext,
      thoughts: agentLevelThoughts,
      toolCalls: groupedToolCalls,
      dataBlocks: collectedDataBlocks,
      newToolHistory,
      sentMessages:
        collectedSentMessages.length > 0 ? collectedSentMessages : undefined,
    };
  },
);

// =============================================================================
// SLICE
// =============================================================================

export const agentSliceV3 = createSlice({
  name: "agentsV3",
  initialState,
  reducers: {
    // Reset current agent
    resetCurrentAgent(state) {
      state.currentAgent = {
        isLoading: false,
        data: null,
        versions: [],
        versionsLoading: false,
      };
    },

    // Reset current version
    resetCurrentVersion(state) {
      state.currentVersion = {
        isLoading: false,
        isSaving: false,
        isPublishing: false,
        data: null,
        working: null,
      };
    },

    // Update working version config field
    updateVersionField<K extends keyof AgentVersionConfig>(
      state: AgentStateV3,
      action: PayloadAction<{ key: K; value: AgentVersionConfig[K] }>,
    ) {
      if (state.currentVersion.working) {
        state.currentVersion.working[action.payload.key] = action.payload.value;
      }
    },

    // Add skill binding
    addSkillBinding(state, action: PayloadAction<SkillBinding>) {
      if (state.currentVersion.working) {
        state.currentVersion.working.skills.push(action.payload);
      }
    },

    // Update skill binding
    updateSkillBinding(
      state,
      action: PayloadAction<{ index: number; binding: SkillBinding }>,
    ) {
      if (state.currentVersion.working) {
        state.currentVersion.working.skills[action.payload.index] =
          action.payload.binding;
      }
    },

    // Remove skill binding
    removeSkillBinding(state, action: PayloadAction<number>) {
      if (state.currentVersion.working) {
        state.currentVersion.working.skills.splice(action.payload, 1);
      }
    },

    // Add agent tool
    addTool(state, action: PayloadAction<AgentToolRef>) {
      if (state.currentVersion.working) {
        if (!state.currentVersion.working.tools) {
          state.currentVersion.working.tools = [];
        }
        state.currentVersion.working.tools.push(action.payload);
      }
    },

    // Update agent tool
    updateTool(
      state,
      action: PayloadAction<{ index: number; tool: AgentToolRef }>,
    ) {
      if (state.currentVersion.working?.tools) {
        state.currentVersion.working.tools[action.payload.index] =
          action.payload.tool;
      }
    },

    // Remove agent tool
    removeTool(state, action: PayloadAction<number>) {
      if (state.currentVersion.working?.tools) {
        state.currentVersion.working.tools.splice(action.payload, 1);
      }
    },

    // Sandbox actions
    addSandboxMessage(state, action: PayloadAction<SandboxMessage>) {
      state.sandbox.messages.push(action.payload);
    },

    clearSandboxMessages(state) {
      state.sandbox.messages = [];
      state.sandbox.chatHistory = [];
      state.sandbox.toolHistory = [];
      state.sandbox.currentThought = null;
      state.sandbox.currentToolCalls = [];
      state.sandbox.pendingThoughts = [];
      state.sandbox.pendingDataBlocks = [];
      state.sandbox.changedMockContextPaths = [];
    },

    setSandboxMockContext(state, action: PayloadAction<MockContext | null>) {
      state.sandbox.mockContext = action.payload;
    },

    selectSandboxScenario(
      state,
      action: PayloadAction<{ id: string; context: MockContext }>,
    ) {
      state.sandbox.selectedScenarioId = action.payload.id;
      state.sandbox.mockContext = structuredClone(action.payload.context);
      state.sandbox.messages = [];
      state.sandbox.chatHistory = [];
      state.sandbox.toolHistory = [];
      state.sandbox.currentThought = null;
      state.sandbox.currentToolCalls = [];
      state.sandbox.pendingThoughts = [];
      state.sandbox.pendingDataBlocks = [];
      state.sandbox.changedMockContextPaths = [];
    },

    clearChangedMockContextPaths(state) {
      state.sandbox.changedMockContextPaths = [];
    },

    setSandboxRunning(state, action: PayloadAction<boolean>) {
      state.sandbox.isRunning = action.payload;
    },

    // Incremental sandbox trace actions
    updateSandboxThought(
      state,
      action: PayloadAction<{
        thought: string;
        complete: boolean;
        durationMs?: number;
      }>,
    ) {
      const { thought, complete, durationMs } = action.payload;
      if (complete) {
        state.sandbox.pendingThoughts.push({
          content: thought,
          durationMs,
          isComplete: true,
        });
        state.sandbox.currentThought = null;
      } else {
        state.sandbox.currentThought = thought;
      }
    },

    updateSandboxToolCall(
      state,
      action: PayloadAction<{
        name: string;
        displayName?: string;
        status: SandboxToolCallStatus;
        args?: Record<string, unknown>;
        durationMs?: number;
        resultSummary?: string;
        isSkillLoad?: boolean;
        skillHandle?: string;
      }>,
    ) {
      const {
        name,
        displayName,
        status,
        args,
        durationMs,
        resultSummary,
        isSkillLoad,
        skillHandle,
      } = action.payload;
      const existingIdx = state.sandbox.currentToolCalls.findIndex((tc) => {
        if (tc.status !== "started" || tc.toolName !== name) return false;
        if (isSkillLoad && skillHandle) {
          return tc.skillHandle === skillHandle;
        }
        return true;
      });

      if (status === "started") {
        state.sandbox.currentToolCalls.push({
          toolCallId: `tc-${Date.now()}-${state.sandbox.currentToolCalls.length}`,
          toolName: name,
          displayName,
          args: args ?? {},
          status: "started",
          isSkillLoad,
          skillHandle,
        });
      } else if (existingIdx >= 0) {
        const tc = state.sandbox.currentToolCalls[existingIdx]!;
        tc.status = status;
        tc.durationMs = durationMs;
        // Include result for skill loads - needed for restoreSkillToolsFromHistory
        tc.result = resultSummary;
        const resolvedDisplayName =
          displayName ??
          (isSkillLoad ? parseDisplayNameFromResult(resultSummary) : undefined);
        if (resolvedDisplayName) {
          tc.displayName = resolvedDisplayName;
        }
      } else {
        state.sandbox.currentToolCalls.push({
          toolCallId: `tc-${Date.now()}-${state.sandbox.currentToolCalls.length}`,
          toolName: name,
          displayName,
          args: args ?? {},
          status,
          durationMs,
          result: resultSummary,
          isSkillLoad,
          skillHandle,
        });
      }
    },

    addSandboxDataBlocks(state, action: PayloadAction<DataBlock[]>) {
      state.sandbox.pendingDataBlocks.push(...action.payload);
    },

    clearSandboxLiveState(state) {
      state.sandbox.currentThought = null;
      state.sandbox.currentToolCalls = [];
      state.sandbox.pendingThoughts = [];
      state.sandbox.pendingDataBlocks = [];
    },

    // Reset current skill
    resetCurrentSkill(state) {
      state.currentSkill = {
        isLoading: false,
        isSaving: false,
        data: null,
        working: null,
      };
    },

    // Update working skill config field
    updateSkillField<K extends keyof SkillConfig>(
      state: AgentStateV3,
      action: PayloadAction<{ key: K; value: SkillConfig[K] }>,
    ) {
      if (state.currentSkill.working) {
        state.currentSkill.working[action.payload.key] = action.payload.value;
      }
    },
  },
  extraReducers: (builder) => {
    // getAgents
    builder
      .addCase(getAgents.pending, (state) => {
        state.agents.isLoading = true;
      })
      .addCase(getAgents.fulfilled, (state, action) => {
        state.agents.isLoading = false;
        state.agents.data = action.payload;
      })
      .addCase(getAgents.rejected, (state) => {
        state.agents.isLoading = false;
      });

    // getAgent
    builder
      .addCase(getAgent.pending, (state) => {
        state.currentAgent.isLoading = true;
      })
      .addCase(getAgent.fulfilled, (state, action) => {
        state.currentAgent.isLoading = false;
        state.currentAgent.data = action.payload;
      })
      .addCase(getAgent.rejected, (state) => {
        state.currentAgent.isLoading = false;
        state.currentAgent.data = null;
      });

    // createAgent
    builder.addCase(createAgent.fulfilled, (state, action) => {
      if (action.payload.agent) {
        state.agents.data.unshift({
          id: action.payload.agent.id,
          handle: action.payload.agent.handle,
          name: action.payload.agent.name,
          description: action.payload.agent.description ?? "",
          createdAt: action.payload.agent.createdAt,
          llmModel: null,
        });
      }
    });

    // deleteAgent
    builder.addCase(deleteAgent.fulfilled, (state, action) => {
      state.agents.data = state.agents.data.filter(
        (a) => a.id !== action.payload,
      );
      if (state.currentAgent.data?.id === action.payload) {
        state.currentAgent.data = null;
      }
    });

    // getAgentVersions
    builder
      .addCase(getAgentVersions.pending, (state) => {
        state.currentAgent.versionsLoading = true;
      })
      .addCase(getAgentVersions.fulfilled, (state, action) => {
        state.currentAgent.versionsLoading = false;
        state.currentAgent.versions = action.payload;
      })
      .addCase(getAgentVersions.rejected, (state) => {
        state.currentAgent.versionsLoading = false;
      });

    // getAgentVersion
    builder
      .addCase(getAgentVersion.pending, (state) => {
        state.currentVersion.isLoading = true;
      })
      .addCase(getAgentVersion.fulfilled, (state, action) => {
        state.currentVersion.isLoading = false;
        state.currentVersion.data = action.payload;
        // Initialize working copy from config
        if (action.payload.config) {
          state.currentVersion.working = { ...action.payload.config };
        } else {
          // Create default config
          state.currentVersion.working = {
            handle: "",
            name: "",
            version: "1.0.0",
            description: "",
            skills: [],
            tools: [],
          };
        }
        // Initialize sandbox mock context and scenario list
        const sandboxRaw = action.payload.config?.sandbox;
        const parsedSandbox = sandboxRaw
          ? SandboxConfigSchema.safeParse(sandboxRaw)
          : null;
        if (parsedSandbox?.success) {
          const scenarioOptions = listSandboxScenariosWithContext(
            parsedSandbox.data,
          ).map((s) => ({
            id: s.id,
            label: s.label,
            context: s.context as MockContext,
          }));
          state.sandbox.scenarioOptions = scenarioOptions;
          const defaultId =
            resolveDefaultSandboxScenarioId(parsedSandbox.data) ?? null;
          state.sandbox.selectedScenarioId = defaultId;
          const initial = scenarioOptions.find((s) => s.id === defaultId);
          state.sandbox.mockContext = initial
            ? structuredClone(initial.context)
            : null;
        } else {
          state.sandbox.scenarioOptions = [];
          state.sandbox.selectedScenarioId = null;
          state.sandbox.mockContext = null;
        }
      })
      .addCase(getAgentVersion.rejected, (state) => {
        state.currentVersion.isLoading = false;
        state.currentVersion.data = null;
        state.currentVersion.working = null;
      });

    // updateAgentVersion
    builder
      .addCase(updateAgentVersion.pending, (state) => {
        state.currentVersion.isSaving = true;
      })
      .addCase(updateAgentVersion.fulfilled, (state) => {
        state.currentVersion.isSaving = false;
      })
      .addCase(updateAgentVersion.rejected, (state) => {
        state.currentVersion.isSaving = false;
      });

    // publishAgentVersion
    builder
      .addCase(publishAgentVersion.pending, (state) => {
        state.currentVersion.isPublishing = true;
      })
      .addCase(publishAgentVersion.fulfilled, (state, action) => {
        state.currentVersion.isPublishing = false;
        if (state.currentVersion.data) {
          state.currentVersion.data.isPublished = true;
          state.currentVersion.data.trafficWeight =
            action.payload.version.trafficWeight;
        }
        // Update in versions list
        const idx = state.currentAgent.versions.findIndex(
          (v) => v.id === action.payload.version.id,
        );
        if (idx >= 0) {
          state.currentAgent.versions[idx] = {
            ...state.currentAgent.versions[idx]!,
            isPublished: true,
            trafficWeight: action.payload.version.trafficWeight,
          };
        }
      })
      .addCase(publishAgentVersion.rejected, (state) => {
        state.currentVersion.isPublishing = false;
      });

    // deleteAgentVersion
    builder.addCase(deleteAgentVersion.fulfilled, (state, action) => {
      state.currentAgent.versions = state.currentAgent.versions.filter(
        (v) => v.id !== action.payload,
      );
      if (state.currentVersion.data?.id === action.payload) {
        state.currentVersion.data = null;
        state.currentVersion.working = null;
      }
    });

    // getAvailableTools
    builder
      .addCase(getAvailableTools.pending, (state) => {
        state.toolsLoading = true;
      })
      .addCase(getAvailableTools.fulfilled, (state, action) => {
        state.toolsLoading = false;
        state.availableTools = action.payload;
      })
      .addCase(getAvailableTools.rejected, (state) => {
        state.toolsLoading = false;
      });

    // getLlmModels
    builder
      .addCase(getLlmModels.pending, (state) => {
        state.modelsLoading = true;
      })
      .addCase(getLlmModels.fulfilled, (state, action) => {
        state.modelsLoading = false;
        state.llmModels = action.payload;
      })
      .addCase(getLlmModels.rejected, (state) => {
        state.modelsLoading = false;
      })
      .addCase(getAvailableSkills.pending, (state) => {
        state.skillsLoading = true;
      })
      .addCase(getAvailableSkills.fulfilled, (state, action) => {
        state.skillsLoading = false;
        state.availableSkills = action.payload;
      })
      .addCase(getAvailableSkills.rejected, (state) => {
        state.skillsLoading = false;
      })
      .addCase(getSkillVersions.pending, (state) => {
        state.skillVersionsLoading = true;
      })
      .addCase(getSkillVersions.fulfilled, (state, action) => {
        state.skillVersionsLoading = false;
        state.skillVersionsBySkill[action.payload.skillHandle] =
          action.payload.versions;
      })
      .addCase(getSkillVersions.rejected, (state) => {
        state.skillVersionsLoading = false;
      })
      .addCase(getSkills.pending, (state) => {
        state.skills.isLoading = true;
      })
      .addCase(getSkills.fulfilled, (state, action) => {
        state.skills.isLoading = false;
        state.skills.data = action.payload;
      })
      .addCase(getSkills.rejected, (state) => {
        state.skills.isLoading = false;
      })
      .addCase(getSkill.pending, (state) => {
        state.currentSkill.isLoading = true;
      })
      .addCase(getSkill.fulfilled, (state, action) => {
        state.currentSkill.isLoading = false;
        state.currentSkill.data = action.payload;
        state.currentSkill.working = {
          handle: action.payload.handle,
          name: action.payload.name,
          description: action.payload.description ?? undefined,
          instructions: action.payload.instructions,
          tools: action.payload.tools,
          examples: action.payload.examples,
          tags: action.payload.tags,
        };
      })
      .addCase(getSkill.rejected, (state) => {
        state.currentSkill.isLoading = false;
      })
      .addCase(createSkill.pending, (state) => {
        state.currentSkill.isSaving = true;
      })
      .addCase(createSkill.fulfilled, (state, action) => {
        state.currentSkill.isSaving = false;
        state.currentSkill.data = action.payload;
        state.skills.data.push({
          id: action.payload.id,
          handle: action.payload.handle,
          name: action.payload.name,
          description: action.payload.description,
          createdAt: action.payload.createdAt,
        });
      })
      .addCase(createSkill.rejected, (state) => {
        state.currentSkill.isSaving = false;
      })
      .addCase(updateSkill.pending, (state) => {
        state.currentSkill.isSaving = true;
      })
      .addCase(updateSkill.fulfilled, (state, action) => {
        state.currentSkill.isSaving = false;
        state.currentSkill.data = action.payload;
        const index = state.skills.data.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (index !== -1) {
          state.skills.data[index] = {
            id: action.payload.id,
            handle: action.payload.handle,
            name: action.payload.name,
            description: action.payload.description,
            createdAt: action.payload.createdAt,
          };
        }
      })
      .addCase(updateSkill.rejected, (state) => {
        state.currentSkill.isSaving = false;
      })
      .addCase(deleteSkill.pending, (state) => {
        state.currentSkill.isSaving = true;
      })
      .addCase(deleteSkill.fulfilled, (state, action) => {
        state.currentSkill.isSaving = false;
        state.currentSkill.data = null;
        state.currentSkill.working = null;
        state.skills.data = state.skills.data.filter(
          (s) => s.id !== action.payload,
        );
      })
      .addCase(deleteSkill.rejected, (state) => {
        state.currentSkill.isSaving = false;
      });

    builder
      .addCase(sendSandboxChatMessage.pending, (state, action) => {
        state.sandbox.isRunning = true;
        // Clear any previous live state
        state.sandbox.currentThought = null;
        state.sandbox.currentToolCalls = [];
        state.sandbox.pendingThoughts = [];
        state.sandbox.pendingDataBlocks = [];
        // Add user message
        const userMessage = action.meta.arg.message;
        state.sandbox.messages.push({
          id: crypto.randomUUID(),
          role: "user",
          content: userMessage,
          timestamp: new Date().toISOString(),
        });
        // Update chat history for context replay
        state.sandbox.chatHistory.push({
          role: "user",
          content: userMessage,
        });
      })
      .addCase(sendSandboxChatMessage.fulfilled, (state, action) => {
        state.sandbox.isRunning = false;
        if (action.payload.mockContext) {
          // Compute changed paths for highlighting before updating mock context
          const changedPaths = getChangedPaths(
            state.sandbox.mockContext,
            action.payload.mockContext,
          );
          state.sandbox.changedMockContextPaths = changedPaths;
          state.sandbox.mockContext = action.payload.mockContext;
        }
        // Add assistant message(s) with trace data
        // If sentMessages is present, add each as a separate message bubble
        if (
          action.payload.sentMessages &&
          action.payload.sentMessages.length > 0
        ) {
          const sentMessages = action.payload.sentMessages;
          // First message gets the tool calls and thoughts (trace data)
          const firstMsg = sentMessages[0];
          if (firstMsg) {
            state.sandbox.messages.push({
              id: firstMsg.id,
              role: "assistant",
              content: firstMsg.content,
              timestamp: firstMsg.timestamp,
              thoughts:
                action.payload.thoughts.length > 0
                  ? action.payload.thoughts
                  : undefined,
              toolCalls:
                action.payload.toolCalls.length > 0
                  ? action.payload.toolCalls
                  : undefined,
              dataBlocks:
                action.payload.dataBlocks.length > 0
                  ? action.payload.dataBlocks
                  : undefined,
              isScheduled: firstMsg.type === "scheduled",
              scheduledFor: firstMsg.scheduledFor,
            });
          }
          // Subsequent messages are plain bubbles (may include scheduled)
          for (let i = 1; i < sentMessages.length; i++) {
            const msg = sentMessages[i];
            if (msg) {
              state.sandbox.messages.push({
                id: msg.id,
                role: "assistant",
                content: msg.content,
                timestamp: msg.timestamp,
                isScheduled: msg.type === "scheduled",
                scheduledFor: msg.scheduledFor,
              });
            }
          }
          // Update chat history with non-scheduled messages only (scheduled are pending)
          const nonScheduledContent = sentMessages
            .filter((m) => m.type !== "scheduled")
            .map((m) => m.content)
            .join("\n\n");
          if (nonScheduledContent) {
            state.sandbox.chatHistory.push({
              role: "assistant",
              content: nonScheduledContent,
            });
          }
        } else {
          // Legacy mode: single message
          state.sandbox.messages.push({
            id: crypto.randomUUID(),
            role: "assistant",
            content: action.payload.assistantContent,
            timestamp: new Date().toISOString(),
            thoughts:
              action.payload.thoughts.length > 0
                ? action.payload.thoughts
                : undefined,
            toolCalls:
              action.payload.toolCalls.length > 0
                ? action.payload.toolCalls
                : undefined,
            dataBlocks:
              action.payload.dataBlocks.length > 0
                ? action.payload.dataBlocks
                : undefined,
          });
          // Update chat history
          state.sandbox.chatHistory.push({
            role: "assistant",
            content: action.payload.assistantContent,
          });
        }
        // Accumulate tool history for multi-turn context replay
        if (action.payload.newToolHistory.length > 0) {
          state.sandbox.toolHistory.push(...action.payload.newToolHistory);
        }
        // Clear live state
        state.sandbox.currentThought = null;
        state.sandbox.currentToolCalls = [];
        state.sandbox.pendingThoughts = [];
        state.sandbox.pendingDataBlocks = [];
      })
      .addCase(sendSandboxChatMessage.rejected, (state, action) => {
        state.sandbox.isRunning = false;
        state.sandbox.messages.push({
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            action.error.message ?? "Failed to get a response from the agent.",
          timestamp: new Date().toISOString(),
        });
        // Clear live state
        state.sandbox.currentThought = null;
        state.sandbox.currentToolCalls = [];
        state.sandbox.pendingThoughts = [];
        state.sandbox.pendingDataBlocks = [];
      });

    // Event subscriptions
    builder
      .addCase(getAgentEventSubscriptions.pending, (state) => {
        state.eventSubscriptions.isLoading = true;
      })
      .addCase(getAgentEventSubscriptions.fulfilled, (state, action) => {
        state.eventSubscriptions.isLoading = false;
        state.eventSubscriptions.data = action.payload;
      })
      .addCase(getAgentEventSubscriptions.rejected, (state) => {
        state.eventSubscriptions.isLoading = false;
      })
      .addCase(createAgentEventSubscription.fulfilled, (state, action) => {
        state.eventSubscriptions.data.unshift(action.payload);
      })
      .addCase(updateAgentEventSubscription.fulfilled, (state, action) => {
        const index = state.eventSubscriptions.data.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (index !== -1) {
          state.eventSubscriptions.data[index] = action.payload;
        }
      })
      .addCase(deleteAgentEventSubscription.fulfilled, (state, action) => {
        state.eventSubscriptions.data = state.eventSubscriptions.data.filter(
          (s) => s.id !== action.payload,
        );
      })
      .addCase(toggleAgentEventSubscription.fulfilled, (state, action) => {
        const index = state.eventSubscriptions.data.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (index !== -1) {
          state.eventSubscriptions.data[index] = action.payload;
        }
      });
  },
});

// =============================================================================
// SELECTORS
// =============================================================================

export const selectAgents = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).agents;
export const selectCurrentAgent = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).currentAgent;
export const selectCurrentVersion = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).currentVersion;
export const selectVersionWorking = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).currentVersion.working;
export const selectSkills = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).skills;
export const selectCurrentSkill = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).currentSkill;
export const selectSandbox = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).sandbox;
export const selectSandboxScenarioOptions = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).sandbox.scenarioOptions;
export const selectSandboxSelectedScenarioId = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).sandbox.selectedScenarioId;
export const selectEventSubscriptions = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).eventSubscriptions;

export const selectChangedMockContextPaths = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).sandbox.changedMockContextPaths;

/**
 * Selector for live agent processing state (for ThreadView agentState prop).
 * Returns null when agent is not processing.
 */
export const selectSandboxAgentState = createSelector(
  [selectSandbox, selectVersionWorking],
  (
    sandbox,
    working,
  ): {
    isProcessing: boolean;
    agentName?: string | null;
    thought?: AgentThought | null;
    toolCalls?: AgentToolCall[];
  } | null => {
    if (!sandbox.isRunning) return null;

    const agentName = working?.persona?.name || working?.name || "Agent";

    // Convert currentThought to AgentThought format
    const thought: AgentThought | null = sandbox.currentThought
      ? { content: sandbox.currentThought, isThinking: true }
      : null;

    // Convert currentToolCalls to AgentToolCall format
    const toolCalls: AgentToolCall[] = sandbox.currentToolCalls.map((tc) => ({
      id: tc.toolCallId,
      name: tc.displayName ?? tc.toolName,
      args: tc.args,
      status:
        tc.status === "started"
          ? "pending"
          : tc.status === "completed"
            ? "success"
            : "error",
      result: tc.result,
      durationMs: tc.durationMs,
    }));

    return {
      isProcessing: true,
      agentName,
      thought,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
    };
  },
);
export const selectAvailableTools = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).availableTools;
export const selectAvailableSkills = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).availableSkills;
export const selectSkillVersionsBySkill = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).skillVersionsBySkill;
export const selectLlmModels = (state: RootState) =>
  (state.agentsV3 as AgentStateV3).llmModels;

/**
 * Selector that converts sandbox messages to a ThreadDetail structure
 * for use with ThreadView component, including REASONING blocks for traces.
 */
export const selectSandboxAsThreadDetail = createSelector(
  [selectSandbox, selectVersionWorking],
  (sandbox, working): ThreadDetail => {
    const agentName = working?.persona?.name || working?.name || "Agent";
    const now = new Date().toISOString();

    const blocks: ThreadBlock[] = [];
    const scheduledMessages: ScheduledMessageSummary[] = [];
    let sequence = 0;

    for (const msg of sandbox.messages) {
      const isUser = msg.role === "user";
      const participantKind = isUser ? "MEMBER" : "AGENT";
      const displayName = isUser ? "You" : agentName;

      // Handle scheduled messages separately - they go to scheduledMessages array
      if (msg.isScheduled && msg.scheduledFor) {
        scheduledMessages.push({
          id: msg.id,
          content: msg.content,
          contentType: "TEXT",
          scheduledAt: msg.scheduledFor,
          status: "PENDING",
          cancelOnActivity: true,
          requiresApproval: false,
          approvedAt: null,
          approvalBlockId: null,
          sender: {
            id: "agent",
            displayName: agentName,
            avatarUrl: null,
            kind: "AGENT",
          },
        });
        continue; // Skip adding to blocks
      }

      // For assistant messages with thoughts/toolCalls, create a REASONING block first
      if (!isUser && (msg.thoughts?.length || msg.toolCalls?.length)) {
        const reasoningId = `${msg.id}-reasoning`;

        const toolCalls = msg.toolCalls || [];
        const childBlocks: ThreadBlock[] = [];
        let globalTcIdx = 0;

        const createToolCallBlock = (
          tc: SandboxToolCall,
          idx: number,
          parentId: string,
          nestedChildren: ThreadBlock[] = [],
        ): ThreadBlock => {
          const isSkillLoad =
            tc.isSkillLoad ?? isSkillLoadToolName(tc.toolName);
          return {
            id: `${msg.id}-tc-${idx}`,
            threadId: "sandbox",
            kind: "TOOL_CALL" as const,
            sequence: idx,
            status:
              tc.status === "completed"
                ? "SUCCESS"
                : tc.status === "failed"
                  ? "ERROR"
                  : null,
            content: isSkillLoad ? null : tc.result || null,
            contentStructured: {
              toolName: tc.toolName,
              displayName: tc.displayName,
              args: tc.args,
              result: isSkillLoad ? undefined : tc.result,
              durationMs: tc.durationMs,
              dataBlocks: tc.dataBlocks,
              isSkillLoad,
              skillHandle: tc.skillHandle,
            },
            createdAt: msg.timestamp,
            updatedAt: msg.timestamp,
            parentBlockId: parentId,
            requiresApproval: false,
            approvalTimeoutMs: null,
            expiresAt: null,
            approvedById: null,
            approvedAt: null,
            approvedBy: null,
            rejectedById: null,
            rejectedAt: null,
            rejectedBy: null,
            rejectionReason: null,
            participant: {
              id: "agent",
              displayName: agentName,
              avatarUrl: null,
              kind: "AGENT" as const,
            },
            message: null,
            agentRun: null,
            workflowRun: null,
            subThread: null,
            threadLink: null,
            childBlocks: nestedChildren,
          };
        };

        for (const tc of toolCalls) {
          const isSkillLoad =
            tc.isSkillLoad ?? isSkillLoadToolName(tc.toolName);
          if (isSkillLoad) {
            const skillBlockId = `${msg.id}-tc-${globalTcIdx}`;
            const nestedChildren: ThreadBlock[] = [];

            // Add skill thoughts as a REASONING block first
            if (tc.thoughts && tc.thoughts.length > 0) {
              nestedChildren.push({
                id: `${skillBlockId}-thoughts`,
                threadId: "sandbox",
                kind: "REASONING" as const,
                sequence: 0,
                status: "SUCCESS",
                content: null,
                contentStructured: {
                  thoughts: tc.thoughts.map((t) => ({
                    content: t.content,
                    durationMs: t.durationMs,
                  })),
                },
                createdAt: msg.timestamp,
                updatedAt: msg.timestamp,
                parentBlockId: skillBlockId,
                requiresApproval: false,
                approvalTimeoutMs: null,
                expiresAt: null,
                approvedById: null,
                approvedAt: null,
                approvedBy: null,
                rejectedById: null,
                rejectedAt: null,
                rejectedBy: null,
                rejectionReason: null,
                participant: {
                  id: "agent",
                  displayName: agentName,
                  avatarUrl: null,
                  kind: "AGENT" as const,
                },
                message: null,
                agentRun: null,
                workflowRun: null,
                subThread: null,
                threadLink: null,
                childBlocks: [],
              });
            }

            // Add child tool calls
            const toolCallChildren = (tc.childToolCalls ?? []).map(
              (childTc, childIdx) =>
                createToolCallBlock(
                  childTc,
                  globalTcIdx + 1 + childIdx,
                  skillBlockId,
                ),
            );
            nestedChildren.push(...toolCallChildren);

            childBlocks.push(
              createToolCallBlock(
                tc,
                globalTcIdx++,
                reasoningId,
                nestedChildren,
              ),
            );
            globalTcIdx += tc.childToolCalls?.length ?? 0;
          } else {
            childBlocks.push(
              createToolCallBlock(tc, globalTcIdx++, reasoningId),
            );
          }
        }

        // Create the REASONING block
        blocks.push({
          id: reasoningId,
          threadId: "sandbox",
          kind: "REASONING" as const,
          sequence: sequence++,
          status: "SUCCESS",
          content: null,
          contentStructured: {
            thoughts: msg.thoughts?.map((t) => ({
              content: t.content,
              durationMs: t.durationMs,
            })),
            dataBlocks: msg.dataBlocks,
          },
          createdAt: msg.timestamp,
          updatedAt: msg.timestamp,
          parentBlockId: null,
          requiresApproval: false,
          approvalTimeoutMs: null,
          expiresAt: null,
          approvedById: null,
          approvedAt: null,
          approvedBy: null,
          rejectedById: null,
          rejectedAt: null,
          rejectedBy: null,
          rejectionReason: null,
          participant: {
            id: "agent",
            displayName: agentName,
            avatarUrl: null,
            kind: "AGENT" as const,
          },
          message: null,
          agentRun: null,
          workflowRun: null,
          subThread: null,
          threadLink: null,
          childBlocks,
        });
      }

      // Create the MESSAGE block
      blocks.push({
        id: msg.id,
        threadId: "sandbox",
        kind: "MESSAGE" as const,
        sequence: sequence++,
        status: null,
        content: msg.content,
        contentStructured: null,
        createdAt: msg.timestamp,
        updatedAt: msg.timestamp,
        parentBlockId: null,
        requiresApproval: false,
        approvalTimeoutMs: null,
        expiresAt: null,
        approvedById: null,
        approvedAt: null,
        approvedBy: null,
        rejectedById: null,
        rejectedAt: null,
        rejectedBy: null,
        rejectionReason: null,
        participant: {
          id: isUser ? "user" : "agent",
          displayName,
          avatarUrl: null,
          kind: participantKind,
        },
        message: {
          id: msg.id,
          content: msg.content,
          contentRaw: null,
          contentType: "TEXT",
          aiGenerated: !isUser,
          sender: {
            id: isUser ? "user" : "agent",
            displayName,
            avatarUrl: null,
            kind: participantKind,
          },
          attachments: [],
        },
        agentRun: null,
        workflowRun: null,
        subThread: null,
        threadLink: null,
        childBlocks: [],
      });
    }

    const lastMessage = sandbox.messages[sandbox.messages.length - 1];

    return {
      id: "sandbox",
      title: `Sandbox - ${agentName}`,
      status: "OPEN",
      kind: "TESTING",
      lastMessageAt: lastMessage?.timestamp ?? now,
      lastMessagePreview: lastMessage?.content?.slice(0, 100) ?? null,
      unreadCount: 0,
      participants: [
        {
          id: "user",
          displayName: "You",
          avatarUrl: null,
          kind: "MEMBER",
          role: "OWNER",
          joinedAt: now,
        },
        {
          id: "agent",
          displayName: agentName,
          avatarUrl: null,
          kind: "AGENT",
          role: "PARTICIPANT",
          joinedAt: now,
        },
      ],
      messages: [],
      createdAt: now,
      blocks,
      scheduledMessages:
        scheduledMessages.length > 0 ? scheduledMessages : undefined,
      executionState: sandbox.isRunning ? "PROCESSING" : "IDLE",
    };
  },
);

// =============================================================================
// EXPORTS
// =============================================================================

export const {
  resetCurrentAgent,
  resetCurrentVersion,
  updateVersionField,
  addSkillBinding,
  updateSkillBinding,
  removeSkillBinding,
  addTool,
  updateTool,
  removeTool,
  addSandboxMessage,
  clearSandboxMessages,
  setSandboxMockContext,
  selectSandboxScenario,
  clearChangedMockContextPaths,
  setSandboxRunning,
  updateSandboxThought,
  updateSandboxToolCall,
  addSandboxDataBlocks,
  clearSandboxLiveState,
  resetCurrentSkill,
  updateSkillField,
} = agentSliceV3.actions;

export default agentSliceV3.reducer;
